import { OnDestroy, DestroyRef, Injectable, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
  FieldValue
} from '@angular/fire/firestore';
import {
  Firestore
} from '@angular/fire/firestore';
import { Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
import { AuthStore } from '@core/services/auth/auth.store';
import { FirestoreApi } from '@core/services/data/firestore-api.service';
import { FirestorePathService } from '@core/services/data/firestore-path.service';
import { FlashbagService } from '../../../../shared/services/flashbag.service';
import { FlashbagType } from '../../../../shared/definitions/flashbag';
import { PropertyStore } from '../../../../shared/services/property.store';
import { WorkspaceStore } from '../../../../shared/services/workspace.store';
import { toDate } from '../../../../shared/utils/to-date';
import { Expense } from '../models/expense.model';
import { IExpensesStore } from './expenses-store.interface';

/**
 * Firestore implementation of the workspace-scoped expenses store.
 *
 * Owns the real-time `collectionData` subscription on the user-scoped
 * expenses sub-collection plus all CRUD + property-name resolution.
 * Replaces the legacy `ExpensesService` (which extended `BaseCrudService`)
 * retired as part of Phase C (2026-04-20).
 *
 * Gating: re-subscribes when the authenticated user or `claimsReady` signal
 * change — matches the original `BaseCrudService.initializeService` behaviour
 * which prevents workspace-scoped reads from firing before the ID token
 * carries the workspace claim.
 */
@Injectable({ providedIn: 'root' })
export class ExpensesFirestoreStore implements IExpensesStore, OnDestroy {
  private readonly collectionName = 'expenses';

  private readonly destroyRef = inject(DestroyRef);
  private readonly firestore = inject(Firestore);
  private readonly fs = inject(FirestoreApi);
  private readonly pathService = inject(FirestorePathService);
  private readonly authStore = inject(AuthStore);
  private readonly flashbagService = inject(FlashbagService);
  private readonly propertyStore = inject(PropertyStore);
  private readonly workspaceStore = inject(WorkspaceStore);

  private readonly _expenses = signal<readonly Expense[]>([]);
  private readonly _loading = signal(false);
  private readonly _lastError = signal<Error | null>(null);

  readonly expenses = this._expenses.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly lastError = this._lastError.asReadonly();

  readonly totalCount = computed(() => this._expenses().length);
  readonly paidCount = computed(() => this._expenses().filter(e => e.isPaid).length);
  readonly unpaidCount = computed(() => this._expenses().filter(e => !e.isPaid).length);
  readonly totalAmount = computed(() => this._expenses().reduce((s, e) => s + (e.amount ?? 0), 0));

  /**
   * L'identifiant d'espace, en flux. Les flux scopés s'y accrochent au lieu de
   * présumer qu'un espace est déjà résolu (#608).
   */
  private readonly workspaceId$ = toObservable(this.workspaceStore.currentWorkspaceId);

  private sub?: Subscription;
  /** (user, workspace) scope the live listener is currently open on — guards
   *  `fetchAll` against re-subscribing (and re-paying the whole collection)
   *  when the scope is unchanged. Mirrors base-crud.service.ts:499. */
  private lastFetchedScopeKey: string | null = null;

  constructor() {
    effect(() => {
      const user = this.authStore.user();
      const claimsReady = this.authStore.claimsReady();
      if (!user?.userUID) {
        this.clearExpenses();
        return;
      }
      if (!claimsReady) return;
      // Bounded: do NOT eagerly stream the whole collection. Detail views use the
      // scoped `expensesForProperty$/Building$` streams; full-list consumers
      // (building financials, the expense right-panel, building overview…) call
      // `refresh()` explicitly.
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  async refresh(force = false): Promise<void> {
    this.fetchAll(force);
  }

  getExpensesForProperty(propertyUID: string): readonly Expense[] {
    return this._expenses().filter(e => e.propertyUID === propertyUID);
  }

  getExpensesForBuilding(buildingUID: string): readonly Expense[] {
    return this._expenses().filter(e => e.buildingUID === buildingUID);
  }

  expensesForProperty$(propertyUID: string): Observable<readonly Expense[]> {
    return this.scopedStream$('propertyUID', propertyUID);
  }

  expensesForBuilding$(buildingUID: string): Observable<readonly Expense[]> {
    return this.scopedStream$('buildingUID', buildingUID);
  }

  getExpenseById(expenseUID: string): Expense | null {
    return this._expenses().find(e => e.expenseUID === expenseUID) ?? null;
  }

  /**
   * Read ONE expense by id via a single document `getDoc` — for detail panels
   * that only need that expense. Avoids `refresh()` (which re-opens the
   * full-collection listener) + `getExpenseById` (find over the whole list):
   * N reads to show 1. Returns null when it doesn't exist or the user is signed
   * out.
   */
  async getExpenseByIdOnce(expenseUID: string): Promise<Expense | null> {
    if (!this.isAuthenticated()) return null;
    const snap = await this.fs.getDoc(this.getDocument(expenseUID));
    if (!snap.exists()) return null;
    return this.buildExpense({ ...snap.data(), expenseUID: snap.id });
  }

  async addExpense(expense: Expense): Promise<string> {
    if (!this.isAuthenticated()) throw new Error('User not authenticated');
    if (!expense.propertyUID?.trim() && !expense.buildingUID?.trim()) {
      throw new Error('Either a property or a building must be linked to an expense');
    }

    try {
      // Resolve property name from the cache so stored docs include a human
      // label (the legacy service did the same — snapshot lookup via the
      // PropertyStore avoids a second Firestore round-trip).
      if (expense.propertyUID && (!expense.propertyName || expense.propertyName.trim() === '')) {
        const name = this.propertyStore.getPropertyNameByUID(expense.propertyUID);
        if (name !== 'Unknown Property') {
          expense.propertyName = name;
        }
      }

      // Firestore rejects `undefined` field values, so strip them before the
      // write. An expense linked only to a property (no building) leaves
      // `buildingUID`/`buildingName` undefined — and several other optional
      // fields (tenantUID, paymentDate, documentUIDs…) are routinely unset.
      // Mirrors the sibling repairs store's `stripUndefined` sanitization.
      const expenseData = stripUndefined(this.addTimestamps({ ...expense }, false));
      const docRef = await this.fs.addDoc(this.getCollection(), expenseData);
      // Optimistic update — prepend so the UI reacts without waiting for the
      // live listener to emit.
      this._expenses.set([{ ...expense, expenseUID: docRef.id } as Expense, ...this._expenses()]);
      this.flashbagService.messageToDisplay('SAVED', 'Expense has been successfully saved', FlashbagType.Success, 4000);
      return docRef.id;
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      this._lastError.set(error);
      this.flashbagService.messageToDisplay('ERROR', 'Failed to save expense', FlashbagType.Danger, 4000);
      throw error;
    }
  }

  async updateExpense(expense: Expense): Promise<void> {
    if (!expense.expenseUID) throw new Error('Expense UID is required for updates');
    if (!this.isAuthenticated()) throw new Error('User not authenticated');

    try {
      const updateData = stripUndefined(this.addTimestamps({ ...expense }, true));
      await this.fs.updateDoc(this.getDocument(expense.expenseUID), updateData as Record<string, unknown>);
      // Optimistic update.
      this._expenses.set(
        this._expenses().map(e =>
          e.expenseUID === expense.expenseUID ? ({ ...e, ...expense } as Expense) : e
        )
      );
      this.flashbagService.messageToDisplay('UPDATED', 'Expense has been successfully updated', FlashbagType.Success, 4000);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      this._lastError.set(error);
      this.flashbagService.messageToDisplay('ERROR', 'Failed to update expense', FlashbagType.Danger, 4000);
      throw error;
    }
  }

  async deleteExpense(expenseUID: string): Promise<void> {
    if (!this.isAuthenticated()) throw new Error('User not authenticated');

    try {
      await this.fs.deleteDoc(this.getDocument(expenseUID));
      // Optimistic update.
      this._expenses.set(this._expenses().filter(e => e.expenseUID !== expenseUID));
      this.flashbagService.messageToDisplay('DELETED', 'Expense has been successfully deleted', FlashbagType.Success, 4000);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      this._lastError.set(error);
      this.flashbagService.messageToDisplay('ERROR', 'Failed to delete expense', FlashbagType.Danger, 4000);
      throw error;
    }
  }

  // --- internals ---

  private fetchAll(force = false): void {
    if (!this.isAuthenticated()) return;
    const scopeKey = `${this.authStore.user()?.userUID}:${this.workspaceStore.currentWorkspaceId() ?? ''}`;
    // Already streaming this exact (user, workspace) scope — the live listener
    // already reflects every change, so re-subscribing only re-pays the whole
    // collection. Skip unless a manual refresh forces it.
    if (!force && this.sub && !this.sub.closed && this.lastFetchedScopeKey === scopeKey) {
      return;
    }
    this.sub?.unsubscribe();
    this.lastFetchedScopeKey = scopeKey;
    this._loading.set(true);
    try {
      const ref = this.getCollection();
      this.sub = this.fs.collectionData(ref, { idField: 'expenseUID' })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
        next: rows => {
          this._expenses.set(rows.map(data => this.buildExpense(data)));
          this._loading.set(false);
        },
        error: err => {
          this._lastError.set(err instanceof Error ? err : new Error(String(err)));
          this._loading.set(false);
        }
      });
    } catch (err) {
      this._lastError.set(err instanceof Error ? err : new Error(String(err)));
      this._loading.set(false);
    }
  }

  /**
   * Map a raw Firestore expense doc into an `Expense` instance: Timestamps
   * coerced to JS Dates (the `date` pipe throws on a raw Timestamp; `new Date()`
   * on one yields Invalid Date) and the property name backfilled from the cache.
   * Shared by the full-list listener AND the scoped streams so both produce
   * identical objects.
   */
  private buildExpense(data: unknown): Expense {
    const d = data as Record<string, unknown>;
    const expense = new Expense(
      d['propertyUID'] as string,
      d['date'] as Date,
      d['type'] as Expense['type'],
      d['amount'] as number,
      d['description'] as string,
      d['chargedTo'] as Expense['chargedTo'],
      d['isPaid'] as boolean,
      d['paymentDate'] as Date | undefined,
      d['documentUIDs'] as string[] | undefined,
      d['tenantUID'] as string | undefined
    );
    Object.assign(expense, d);
    expense.date = toDate(d['date']) ?? expense.date;
    if (d['paymentDate']) expense.paymentDate = toDate(d['paymentDate']) ?? undefined;
    if (d['createdAt']) expense.createdAt = toDate(d['createdAt']) ?? expense.createdAt;
    if (d['updatedAt']) expense.updatedAt = toDate(d['updatedAt']) ?? expense.updatedAt;
    if (expense.propertyUID && (!expense.propertyName || expense.propertyName.trim() === '')) {
      const name = this.propertyStore.getPropertyNameByUID(expense.propertyUID);
      if (name !== 'Unknown Property') expense.propertyName = name;
    }
    return expense;
  }

  /**
   * Reactive stream of expenses matching ONE field (`propertyUID`/`buildingUID`)
   * via a server-side `where`, so a detail view reads only its own entity's
   * expenses instead of the whole workspace. Single-field equality → auto-indexed.
   * No fallback to the full-list filtered stream: a query error surfaces so the
   * scoped read can be validated (it must not silently scan the whole collection).
   */
  /**
   * Flux scopé sur un champ, ATTENDU l'espace de travail (#608).
   *
   * La requête était construite au moment de l'appel. `getCollection()` passe
   * par `pathService.getWorkspaceCollection()`, qui LÈVE une exception tant
   * qu'aucun espace n'est résolu — l'appel jetait donc avant même le
   * `subscribe` du consommateur. Sur la page Dépenses atteinte par une URL
   * directe, cela laissait « Chargement des dépenses… » à l'écran pour
   * toujours : `loading` n'avait aucun moyen de retomber. En navigation interne
   * l'espace était déjà là, d'où un défaut invisible à l'usage courant.
   *
   * On s'accroche donc à l'espace et on ne bâtit la requête qu'une fois qu'il
   * existe. Changer d'espace reconstruit le flux, ce que la version précédente
   * ne faisait pas non plus.
   */
  private scopedStream$(
    field: 'propertyUID' | 'buildingUID',
    value: string
  ): Observable<readonly Expense[]> {
    return this.workspaceId$.pipe(
      filter((wid): wid is string => !!wid),
      distinctUntilChanged(),
      switchMap(() => {
        const q = this.fs.query(this.getCollection(), this.fs.where(field, '==', value));
        return this.fs.collectionData(q, { idField: 'expenseUID' }) as Observable<unknown[]>;
      }),
      map(rows => rows.map(r => this.buildExpense(r)))
    );
  }

  private clearExpenses(): void {
    this.sub?.unsubscribe();
    this.lastFetchedScopeKey = null;
    this._expenses.set([]);
  }

  private getCollection() {
    return this.fs.collection(this.firestore, this.pathService.getWorkspaceCollection(this.collectionName));
  }

  private getDocument(expenseUID: string) {
    return this.fs.doc(this.firestore, this.pathService.getWorkspaceDocument(this.collectionName, expenseUID));
  }

  private isAuthenticated(): boolean {
    return !!this.authStore.user()?.userUID;
  }

  private addTimestamps<T extends Record<string, unknown>>(data: T, isUpdate = false): T & {
    createdAt?: FieldValue;
    updatedAt: FieldValue;
  } {
    const result = { ...data, updatedAt: this.fs.serverTimestamp() };
    if (!isUpdate) {
      (result as Record<string, unknown>)['createdAt'] = this.fs.serverTimestamp();
    }
    return result;
  }
}

/**
 * Drop keys whose value is `undefined` so the payload always serializes —
 * Firestore throws on `undefined` field values. Shallow by design (Expense
 * has no nested objects); `serverTimestamp()` / other FieldValue sentinels are
 * preserved. Mirrors `stripUndefined` in the repairs store.
 */
export function stripUndefined<T extends Record<string, unknown>>(obj: T): T {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) out[key] = value;
  }
  return out as T;
}
